import {
  conversations,
  normalizeAnswerContent,
  SearchConversationResponse,
  Step,
  WebSearchSettings,
} from '@api';
import { generatePath } from '@helpers';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { apiFetch, wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

import { Conversation, ConversationTask } from '../use-chat/use-chat.types';

export const FETCH_ALL_CONVERSATIONS_PAGE_SIZE = 50;

export type SubmittedAnalysisMode = 'Deep' | 'Auto';
export type WebSearch = 'restricted' | 'open' | 'block';

const normalizeStep = (step?: Step): Step | undefined => {
  if (!step) {
    return undefined;
  }

  return {
    ...step,
    content: normalizeAnswerContent(step.content),
  };
};

const normalizeTask = (task: ConversationTask): ConversationTask => ({
  ...task,
  total_seconds: task.total_seconds ?? task.duration_seconds,
  output:
    task.output === undefined
      ? task.output
      : normalizeAnswerContent(task.output),
  first_state: normalizeStep(task.first_state),
  last_state: normalizeStep(task.last_state),
  states: task.states?.map(step => normalizeStep(step) ?? step),
});

const normalizeConversation = (conversation: Conversation): Conversation => ({
  ...conversation,
  tasks: conversation.tasks?.map(normalizeTask),
});

const normalizeSearchConversationResponse = (
  response: SearchConversationResponse
): SearchConversationResponse => ({
  ...response,
  results: response.results.map(result => ({
    ...result,
    state: normalizeStep(result.state) ?? result.state,
  })),
});

export interface GroupFilter {
  policy: 'Block';
  groups: string[];
}

export interface WebSearchToolsConfig {
  group_filter?: GroupFilter;
  parameters: { web_search: { sources: string[] } };
}

type BasePostConversationArgs = {
  message: string;
  analysis_mode?: SubmittedAnalysisMode;
  tools_config?: {
    group_filter?: GroupFilter;
    parameters: {
      web_search?: {
        sources: string[];
      };
    };
  };
  attachments?: string[];
  controller?: AbortController;
};
type PostContinueConversationArgs = BasePostConversationArgs & {
  conversationId: string;
};

export const postConversation = async ({
  message,
  analysis_mode,
  tools_config,
  attachments,
  controller,
}: BasePostConversationArgs) => {
  return apiFetch(conversations.postConversation.path, {
    schema: conversations.postConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tools_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const postContinueConversation = async ({
  message,
  conversationId,
  analysis_mode,
  controller,
  tools_config,
  attachments,
}: PostContinueConversationArgs) => {
  return apiFetch(conversations.continueConversation.path(conversationId), {
    schema: conversations.continueConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tools_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const getConversationTasks = async (
  id: string,
  signal?: AbortSignal
) => {
  let page = 1;
  const allTasks: ConversationTask[] = [];
  let conversationData: Conversation | undefined = undefined;
  let hasMorePages = true;

  while (hasMorePages) {
    const requestPath = generatePath({
      basePath: conversations.getConversationTask.path(id),
      params: {
        page,
      },
    });

    const result = await apiFetch(requestPath, {
      schema: conversations.getConversationTask.schema,
      signal,
    });

    if (result.error) {
      return result;
    }

    if (page === 1 && result.data) {
      conversationData = normalizeConversation({
        ...result.data,
        public_copies: [],
      });
    }

    if (result.data?.tasks && result.data.tasks.length > 0) {
      allTasks.push(...result.data.tasks.map(normalizeTask));
      page++;
    } else {
      hasMorePages = false;
    }
  }

  return {
    data: conversationData
      ? { ...conversationData, tasks: allTasks }
      : undefined,
  };
};

export const searchConversation = (query: string, page: number) => {
  const requestPath = generatePath({
    basePath: conversations.searchConversation.path,
    params: {
      q: query,
      page,
    },
  });

  return wretchRequest<SearchConversationResponse>(requestPath, {
    schema: conversations.searchConversation.schema,
  }).then(normalizeSearchConversationResponse);
};

type GetConversationTaskStepsArgs = {
  conversationId: string;
  taskId: string;
};

export const getConversationTaskSteps = ({
  conversationId,
  taskId,
}: GetConversationTaskStepsArgs) => {
  return apiFetch(
    conversations.getConversationMessage.path(conversationId, taskId),
    {
      schema: conversations.getConversationMessage.schema,
    }
  ).then(response => {
    if (response.data) {
      return {
        ...response,
        data: {
          ...response.data,
          states: response.data.states.map(step => normalizeStep(step) ?? step),
        },
      };
    }

    return response;
  });
};

export const updateConversation = ({
  id,
  name,
  favourite,
}: {
  id: string;
  name?: string;
  favourite?: boolean;
}) => {
  const body = favourite !== undefined ? { name, favourite } : { name };
  return apiFetch(conversations.conversationId.path(id), {
    method: 'put',
    body,
  });
};

export const deleteConversation = (id: string) =>
  apiFetch(conversations.conversationId.path(id), {
    method: 'delete',
  });

export const getAllConversations = (page: number) => {
  const isV3 = useFlags
    .getState()
    .getFeatureFlagState('REASON-1897-conversation-v3');
  const requestPath = generatePath({
    basePath: isV3 ? 'conversation/v3' : conversations.getAllConversations.path,
    params: {
      page,
      page_size: FETCH_ALL_CONVERSATIONS_PAGE_SIZE,
    },
  });

  return wretchRequest(requestPath, {
    schema: conversations.getAllConversations.schema,
  });
};

export const postFeedback = (body: {
  conversation_id: string;
  request_id: string;
  feedback: string;
}) => {
  wretchRequest(conversations.feedback.path, {
    method: 'post',
    body,
  });
};

export const abortRequest = (id: string) =>
  apiFetch(conversations.abortConversation.path(id), {
    method: 'put',
    body: { action: 'cancel' },
  });

export const copyConversation = (id: string, name: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'public',
      name,
    },
  });

export const postClonePublicConversation = (id: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'private',
    },
  });

export const getCustomSources = () =>
  apiFetch(conversations.getCustomSources.path, {
    schema: conversations.getCustomSources.schema,
  });

export const updateCustomSources = (sources: WebSearchSettings) =>
  apiFetch(conversations.updateCustomSources.path, {
    method: 'put',
    body: sources,
  });

export const resolveWebSearchConfig = async (
  webSearch: WebSearch
): Promise<WebSearchToolsConfig> => {
  if (webSearch === 'restricted') {
    const result = await getCustomSources();
    return {
      parameters: { web_search: { sources: result.data?.sources ?? [] } },
      group_filter: undefined,
    };
  }
  if (webSearch === 'block') {
    return {
      parameters: { web_search: { sources: [] } },
      group_filter: { policy: 'Block', groups: ['web'] },
    };
  }
  return {
    parameters: { web_search: { sources: [] } },
    group_filter: undefined,
  };
};
